import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { LoadingIndicator, ProtectedRoute } from '@leanpay/ui'

import { useAppStore } from 'app/app-context'
import { VendorUserRole } from 'library/constants'

// Lazy load all route components for code splitting
const NavigationMenu = lazy(() =>
    import('app/components/navigation-menu/navigation-menu').then((m) => ({
        default: m.NavigationMenu,
    })),
)
const About = lazy(() => import('app/shared/about/about'))
const Company = lazy(() => import('app/shared/company/company'))
const ContactAndSupport = lazy(() => import('app/shared/contact-and-support/contact-and-support'))
const ForgotPassword = lazy(() => import('app/shared/forgot-password/forgot-password'))
const Login = lazy(() => import('app/shared/login/login'))
const OfferDetails = lazy(() => import('app/shared/offers/offer-details'))
const Offers = lazy(() => import('app/shared/offers/offers'))
const PasswordSet = lazy(() => import('app/shared/password-set/password-set'))
const PaymentAndInvoices = lazy(
    () => import('app/shared/payment-and-invoices/payment-and-invoices'),
)
const PaymentInvoiceDetails = lazy(
    () => import('app/shared/payment-and-invoices/payment-invoice-details'),
)
const RemoteOffer = lazy(() => import('app/shared/remote-offer/remote-offer'))
const TransactionDetails = lazy(() => import('app/shared/transactions/transaction-details'))
const Transactions = lazy(() => import('app/shared/transactions/transactions'))
const UserManagement = lazy(() => import('app/shared/user-management/user-management'))
const VerifyEmail = lazy(() => import('app/shared/verify-email/verify-email'))

const MainAppHUN = () => {
    const { isAuthenticated, userRoles } = useAppStore()

    const isAdminRoute = userRoles?.includes(VendorUserRole.ROLE_ADMIN)
    const isAgentOnly = userRoles?.length === 1 && userRoles[0] === VendorUserRole.ROLE_AGENT

    return (
        <Suspense fallback={<LoadingIndicator />}>
            <Routes>
                <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/password-reset" element={<PasswordSet />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route
                    element={<ProtectedRoute isAllowed={isAuthenticated} redirectPath="/login" />}
                >
                    <Route element={<NavigationMenu />}>
                        {/* Admin-only routes */}
                        <Route
                            element={<ProtectedRoute isAllowed={!!isAdminRoute} redirectPath="/" />}
                        >
                            <Route path="/verify-email" element={<VerifyEmail />} />
                            <Route path="/company" element={<Company />} />
                            <Route path="/user-management" element={<UserManagement />} />
                            <Route path="/payment-and-invoices" element={<PaymentAndInvoices />} />
                            <Route
                                path="/payment-invoice-details/:id"
                                element={<PaymentInvoiceDetails />}
                            />
                        </Route>
                        <Route index element={<Navigate to="/remote-offer" />} />
                        <Route path="/remote-offer" element={<RemoteOffer />} />

                        {/* Restricted routes for agent-only users */}
                        <Route
                            element={
                                <ProtectedRoute
                                    isAllowed={!isAgentOnly}
                                    redirectPath="/remote-offer"
                                />
                            }
                        >
                            <Route path="/transactions" element={<Transactions />} />
                            <Route path="/transactions/:id" element={<TransactionDetails />} />
                        </Route>

                        <Route path="/about" element={<About />} />
                        <Route path="/contact-and-support" element={<ContactAndSupport />} />

                        <Route path="/offers" element={<Offers />} />
                        <Route path="/offers/:id" element={<OfferDetails />} />
                        <Route path="*" element={<Navigate to="/remote-offer" />} />
                    </Route>
                </Route>
            </Routes>
        </Suspense>
    )
}

export default MainAppHUN
